/// <amd-module name="typeahead/core/SuggestionsAggregator" />
import {ISuggester, ISuggesterResponse} from "typeahead/core/ISuggester";
import {ISuggestionResult} from "typeahead/core/Suggestion";

export enum SuggesterId {RecentHistorySuggester, FlightBookingSuggester, CurrentLocationSuggester, OnFocusSuggester};

export interface ISuggesterConfig {
    rank: number;
    isEnabled: boolean;
}

export abstract class SuggestionsAggregator implements ISuggester {

    private static readonly LOWEST_RANK: number = 999;
    private suggesterConfig: {[suggester: number]: ISuggesterConfig} = {
        [SuggesterId.RecentHistorySuggester]: this.getRecentHistorySuggesterConfig(),
        [SuggesterId.FlightBookingSuggester]: this.getFlightBookingSuggesterConfig(),
        [SuggesterId.CurrentLocationSuggester]: this.getCurrentLocationSuggesterConfig(),
    };
    private emptySuggesterResponse: ISuggesterResponse = {id: this.getId(), results: []};
    constructor(private suggesters: ISuggester[]) {}

    public suggest(options: any, context: IContext): JQueryPromise <ISuggesterResponse> {
        let deferred: JQueryDeferred <ISuggesterResponse> = $.Deferred();
        const that = this;
        /* tslint:disable-next-line: only-arrow-functions
         * arguments object is not available in arrow functions in ES3 and ES5.
         * arguments object contains the list of responses that are passed as parameters to this callback */
        const handleSuggestersPromises = function() {
            let responses: ISuggesterResponse[] = Array.prototype.slice.call(arguments);
            deferred.resolve(that.aggregate(responses));
        };
        if (this.suggesters && this.suggesters.length) {
            let suggestersPromises: Array <JQueryPromise <ISuggesterResponse>> =
                this.suggesters.map((suggester: ISuggester) => {
                    return suggester.suggest(options, context);
                });
            $.when.apply($, suggestersPromises).done(handleSuggestersPromises);
        } else {
            deferred.resolve(this.emptySuggesterResponse);
        }
        return deferred.promise();
    }

    public abstract getId(): SuggesterId;

    protected abstract getRecentHistorySuggesterConfig(): ISuggesterConfig;
    protected abstract getFlightBookingSuggesterConfig(): ISuggesterConfig;
    protected abstract getCurrentLocationSuggesterConfig(): ISuggesterConfig;

    protected getMaxResults(): number {
        return 10;
    }

    private getSuggesterConfig(response: ISuggesterResponse): ISuggesterConfig {
        return this.suggesterConfig[response.id] ? this.suggesterConfig[response.id] :
            {rank: SuggestionsAggregator.LOWEST_RANK, isEnabled: false};
    }

    private aggregate(responses: ISuggesterResponse[]): ISuggesterResponse {
        if (responses) {
            responses = this.filter(responses);
            this.sort(responses);
        }
        return {id: this.getId(), results: this.getAggregatedSuggestionResults(responses)};
    }

    private filter (responses: ISuggesterResponse[]) {
        if (responses) {
            return responses.filter((response: ISuggesterResponse) => {
                return this.getSuggesterConfig(response).isEnabled;
            });
        }
        return responses;
    }

    private sort(responses: ISuggesterResponse[]) {
        if (responses) {
            responses.sort((a: ISuggesterResponse, b: ISuggesterResponse) => {
                return this.getSuggesterConfig(a).rank - this.getSuggesterConfig(b).rank;
            });
        }
    }

    private getAggregatedSuggestionResults(responses: ISuggesterResponse[]): ISuggestionResult[] {
        let aggregatedResults: ISuggestionResult[] = [];
        if (responses) {
            responses.forEach((response: ISuggesterResponse) => {
                Array.prototype.push.apply(aggregatedResults, response.results);
            });
            aggregatedResults = aggregatedResults.slice(0, this.getMaxResults());
            for (let i = 0; i < aggregatedResults.length; i++) {
                aggregatedResults[i].index = i + "";
            }
        }
        return aggregatedResults;
    }
}
